import React, { useState } from 'react';
import { ChevronDown, Plus, Check, Building2 } from 'lucide-react';
import { useClient } from '../context/ClientContext';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

// Check if a logo_url is a direct displayable URL (not a file ID)
function isDisplayableUrl(url) {
  return url && (url.startsWith('/') || url.startsWith('http'));
}

export function ClientSelector() {
  const { clients, activeClient, resolvedLogoUrl, switchClient, addClient } = useClient();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  if (!activeClient) return null;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const slug = newSlug.trim() || newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
      await addClient({
        name: newName.trim(),
        slug,
        primary_color: '#1F467C',
        secondary_color: '#5CB85C',
        accent_color: '#3980C3',
        hashtags: [],
        social_handles: {},
      });
      toast({ title: 'Client created', description: `${newName.trim()} workspace is ready` });
      setShowCreate(false);
      setNewName('');
      setNewSlug('');
      setOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create client';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="relative" data-testid="client-selector">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/80 border border-gray-200 hover:border-gray-300 transition-colors text-sm"
          data-testid="client-selector-trigger"
        >
          {resolvedLogoUrl ? (
            <img src={resolvedLogoUrl} alt="" className="w-5 h-5 object-contain rounded" />
          ) : (
            <Building2 className="w-4 h-4 text-gray-400" />
          )}
          <span className="font-semibold text-gray-700 max-w-[100px] lg:max-w-[140px] truncate hidden sm:inline">
            {activeClient.name}
          </span>
          <span className="font-semibold text-gray-700 max-w-[100px] truncate sm:hidden">
            {activeClient.slug.toUpperCase()}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="client-dropdown">
              <div className="p-2 border-b border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 py-1">Workspaces</p>
              </div>
              <div className="max-h-60 overflow-y-auto p-1">
                {clients.map(client => (
                  <button
                    key={client.id}
                    onClick={() => { switchClient(client.id); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeClient.id === client.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                    data-testid={`client-option-${client.slug}`}
                  >
                    {isDisplayableUrl(client.logo_url) ? (
                      <img src={client.logo_url} alt="" className="w-6 h-6 object-contain rounded" />
                    ) : (
                      <div className="w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: client.primary_color || '#1F467C' }}
                      >
                        {client.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{client.name}</p>
                      <p className="text-[10px] text-gray-400">{client.slug}</p>
                    </div>
                    {activeClient.id === client.id && (
                      <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <div className="p-2 border-t border-gray-100">
                <button
                  onClick={() => { setShowCreate(true); setOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                  data-testid="add-client-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add New Client
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Client Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[400px]" data-testid="create-client-modal">
          <DialogTitle className="text-lg font-bold text-gray-900">New Client Workspace</DialogTitle>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Client Name</label>
              <Input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30));
                }}
                placeholder="e.g., Mountain View Winery"
                data-testid="new-client-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Slug (URL-friendly)</label>
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, ''))}
                placeholder="e.g., mountain-view"
                data-testid="new-client-slug"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="text-white"
              style={{ backgroundColor: 'var(--brand-primary)' }}
              data-testid="confirm-create-client-btn"
            >
              {creating ? 'Creating...' : 'Create Workspace'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
